import { MathJax, MathJaxContext } from "better-react-mathjax";
import { useDispatch, useSelector } from "react-redux";
import Popup from "../../Popup";
import {
  checkQuestionAPI,
  deletePreviewExcelRow,
  updatePreviewExcelRow,
  setState,
  setNewQuestionForm,
  fetchThemes,
  fetchSubjects,
  fetchQuizTypes,
  fetchSubjectBasedClassesData,
  submitReviewedQuestions,
} from "./redux/schoolQuizSlice";
import { difficultyOptions } from "./SchoolQuizQuestion";
import { useEffect, useState } from "react";
import axios from "axios";
import { SCHOOL_QUIZ_API_BASE_URL } from "../config";

function QuestionsTable({ data = [], onEdit, onDelete }) {
  const getStatus = (item) =>
    (item.predicted_status || item.status || "").toLowerCase().trim();

  const getRowColor = (status) => {
    switch (status) {
      case "insert":
        return "#dcfce7";
      case "skip":
      case "skipped":
        return "#fee2e2";
      case "update":
        return "#fef9c3";
      case "delete":
        return "#ffedd5";
      default:
        return "#ffffff";
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "insert":
        return { backgroundColor: "#bbf7d0", color: "#166534" };
      case "skip":
      case "skipped":
        return { backgroundColor: "#fecaca", color: "#991b1b" };
      case "update":
        return { backgroundColor: "#fef08a", color: "#854d0e" };
      case "delete":
        return { backgroundColor: "#fed7aa", color: "#9a3412" };
      default:
        return { backgroundColor: "#e5e7eb", color: "#374151" };
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "insert":
        return "Correct";
      case "skip":
      case "skipped":
        return "Skipped";
      case "update":
        return "Update";
      case "delete":
        return "Delete";
      default:
        return status || "Unknown";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "insert":
        return "bi-check-circle-fill";
      case "skip":
      case "skipped":
        return "bi-exclamation-circle-fill";
      case "update":
        return "bi-pencil-fill";
      case "delete":
        return "bi-trash-fill";
      default:
        return "bi-question-circle";
    }
  };

  return (
    <MathJaxContext>
      <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
        <table className="min-w-full divide-y divide-gray-200 text-sm upload-table">
          <thead className="bg-gray-50">
            <tr>
              {[
                "S.No",
                "Question",
                "Option A",
                "Option B",
                "Option C",
                "Option D",
                "Correct",
                "Status / Reason",
                "Actions",
              ].map((heading, index) => (
                <th
                  key={heading}
                  className={`px-4 py-3 font-semibold text-gray-700 ${
                    index === 8 ? "text-center" : "text-left"
                  }`}
                  style={{
                    width:
                      index === 0
                        ? "15px"
                        : index === 6
                          ? "90px"
                          : index === 7
                            ? "190px"
                            : index === 8
                              ? "110px"
                              : undefined,
                    minWidth: index === 1 ? "280px" : undefined,
                  }}
                >
                  {heading}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200">
            {data.length > 0 ? (
              data.map((item, index) => {
                const status = getStatus(item);
                const isSkip = status === "skip" || status === "skipped";

                return (
                  <tr
                    key={item.id ?? item.row ?? index}
                    style={{
                      backgroundColor: getRowColor(status),
                    }}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">{index + 1}</td>

                    <td className="px-4 py-3 min-w-[280px]">
                      <MathJax dynamic>{item.question || "-"}</MathJax>
                    </td>

                    {["option_A", "option_B", "option_C", "option_D"].map(
                      (option) => (
                        <td key={option} className="px-4 py-3">
                          <MathJax dynamic>{item[option] || "-"}</MathJax>
                        </td>
                      ),
                    )}

                    <td className="px-4 py-3 font-semibold text-gray-800">
                      {item.correct_option || "-"}
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex flex-col items-start gap-1.5">
                        <span
                          style={{
                            ...getStatusStyle(status),
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                            padding: "5px 9px",
                            borderRadius: "20px",
                            fontSize: "12px",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          <i
                            className={`bi ${getStatusIcon(status)}`}
                            aria-hidden="true"
                          />
                          {getStatusLabel(status)}
                        </span>

                        {isSkip && (
                          <span
                            style={{
                              color: "#991b1b",
                              fontSize: "12px",
                              overflowWrap: "anywhere",
                            }}
                          >
                            <strong>Reason:</strong>{" "}
                            {item.reason || "No reason provided"}
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => onEdit?.(item)}
                          title="Edit question"
                          aria-label="Edit question"
                          style={{
                            color: "#2563eb",
                            backgroundColor: "#eff6ff",
                            border: "1px solid #bfdbfe",
                            borderRadius: "6px",
                            padding: "7px 9px",
                            cursor: "pointer",
                          }}
                        >
                          <i className="bi bi-pencil-square" />
                        </button>

                        <button
                          type="button"
                          onClick={() => onDelete?.(item)}
                          title="Delete question"
                          aria-label="Delete question"
                          style={{
                            color: "#dc2626",
                            backgroundColor: "#fef2f2",
                            border: "1px solid #fecaca",
                            borderRadius: "6px",
                            padding: "7px 9px",
                            cursor: "pointer",
                          }}
                        >
                          <i className="bi bi-trash3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={9}
                  className="px-4 py-10 text-center text-gray-500"
                >
                  <i className="bi bi-inbox mr-2" />
                  No questions available to preview.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </MathJaxContext>
  );
}

export default function QuestionPreview({
  open,
  onClose,
  excelFileName,
  onSubmit,
}) {
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const {
    previewExcelData,
    subQuizType,
    newQuestionForm,
    selectedSubThemes,
    quizType,
    quizTypesList,
    subjectsList,
    subjectBasedClasses,
    themes = [],
    subThemes = [],
    loading,
  } = useSelector((state) => state.schoolQuiz || {});

  const questions =
    previewExcelData?.rows?.length > 0 ? previewExcelData.rows : [];

  const [groups, setGroups] = useState([]);
  const [editingQuestion, setEditingQuestion] = useState(null); // null = show table
  const userStr = localStorage.getItem("user");
  const userObj = userStr ? JSON.parse(userStr) : {};
  const userId = userObj?.id ?? userObj?.user_id ?? "";

  const fetchGroups = async () => {
    try {
      const res = await axios.get(`${SCHOOL_QUIZ_API_BASE_URL}/groups`);
      setGroups(res.data || []);
    } catch (err) {
      console.error("fetchGroups error:", err);
    }
  };

  useEffect(() => {
    if (open) {
      dispatch(fetchThemes());
      dispatch(fetchSubjects());
      dispatch(fetchQuizTypes());
      fetchGroups();
    }
  }, [open, dispatch]);

  useEffect(() => {
    const subject_id = newQuestionForm?.subject_id;

    if (subject_id) {
      // Only fetch classes – DO NOT clear class_id
      dispatch(
        fetchSubjectBasedClassesData({
          subject_id: Number(subject_id),
        }),
      );
    }
  }, [dispatch, newQuestionForm?.subject_id]);
  const handleThemeChange = (e) => {
    const value = e.target.value;
    dispatch(setNewQuestionForm({ key: "theme_id", value }));
    dispatch(setState({ key: "selectedSubThemes", value: [] }));
    const filtered =
      themes.find((t) => t.theme_id === Number(value))?.sub_theme || [];
    dispatch(setState({ key: "subThemes", value: filtered }));
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    dispatch(setNewQuestionForm({ key: name, value }));
  };

  // ========== UPDATE (Save Changes) ==========
  const handleUpdateQuestion = async () => {
    if (!editingQuestion) return;

    try {
      let payload = {
        quizType: subQuizType,
        question: editingQuestion.question,
        correct_option: editingQuestion.correct_option,
        option_A: editingQuestion.option_A,
        option_B: editingQuestion.option_B,
        option_C: editingQuestion.option_C,
        option_D: editingQuestion.option_D,
        level:
          editingQuestion.level || newQuestionForm?.difficulty_level || "basic",
      };

      if (subQuizType === 1 || subQuizType === 2) {
        payload = {
          ...payload,
          class_id: newQuestionForm?.class_id
            ? Number(newQuestionForm.class_id)
            : undefined,
          subject_id: newQuestionForm?.subject_id
            ? Number(newQuestionForm.subject_id)
            : undefined,
        };
      } else if (subQuizType === 3 || subQuizType === 4) {
        payload = {
          ...payload,
          theme_id: newQuestionForm.theme_id
            ? selectedSubThemes?.length
              ? selectedSubThemes
              : [newQuestionForm.theme_id]
            : [],
          group_id: newQuestionForm?.group_id
            ? Number(newQuestionForm.group_id)
            : undefined,
        };
      }

      Object.keys(payload).forEach(
        (key) => payload[key] == null && delete payload[key],
      );

      // ✅ Call the function directly (no dispatch)
      const response = await dispatch(checkQuestionAPI(payload));

      dispatch(
        updatePreviewExcelRow({
          row: editingQuestion.row,
          updatedData: {
            ...editingQuestion,
            predicted_status:
              response.predicted_status || response.payload?.status,
            reason: response?.payload.reason || null,
          },
        }),
      );

      setEditingQuestion(null);
    } catch (err) {
      console.error("Failed to update question:", err);
      alert(err?.message || "Failed to update question");
    }
  };

  // ========== DELETE ==========
  const handleDeleteQuestion = (question) => {
    if (!window.confirm("Are you sure you want to delete this question?"))
      return;
    dispatch(deletePreviewExcelRow(question.row));
  };

  // ========== CANCEL ==========
  const handleCancelEdit = () => {
    setEditingQuestion(null); // just go back to table, no API call
  };

  const handleSubmitReviewedQuestions = async () => {
    if (!questions.length) {
      alert("No questions to submit");
      return;
    }

    try {
      setSubmitting(true);

      // Get the original Excel file name
      // You need to pass it from the parent or store it in Redux

      // Build base payload
      let payload = {
        quizType: subQuizType,
        created_by: userId ? Number(userId) : undefined,
        filename: excelFileName || "", // ← added
        rows: questions.map((q) => ({
          question: q.question,
          correct_option: q.correct_option,
          option_A: q.option_A,
          option_B: q.option_B,
          option_C: q.option_C,
          option_D: q.option_D,
          level: q.level || newQuestionForm?.difficulty_level || "basic",
        })),
      };

      // Conditional fields
      if (subQuizType === 1 || subQuizType === 2) {
        payload = {
          ...payload,
          class_id: newQuestionForm?.class_id
            ? Number(newQuestionForm.class_id)
            : undefined,
          subject_id: newQuestionForm?.subject_id
            ? Number(newQuestionForm.subject_id)
            : undefined,
        };
      } else if (subQuizType === 3 || subQuizType === 4) {
        payload = {
          ...payload,
          theme_id: newQuestionForm.theme_id
            ? selectedSubThemes?.length
              ? selectedSubThemes
              : [newQuestionForm.theme_id]
            : [],
          group_id: newQuestionForm?.group_id
            ? Number(newQuestionForm.group_id)
            : undefined,
        };
      }

      // Clean null / undefined
      Object.keys(payload).forEach(
        (key) => payload[key] == null && delete payload[key],
      );
      const res = await dispatch(submitReviewedQuestions(payload)).unwrap();

      alert(
        res?.message ||
          `Successfully submitted ${questions.length} question(s)`,
      );
      onSubmit();
    } catch (err) {
      console.error("Submit reviewed questions failed:", err);
      alert(err?.message || "Failed to submit questions");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <>
      {open && (
        <Popup
          title={editingQuestion ? "Edit Question" : "Excel Preview"}
          onClose={onClose}
          maxWidth="2000px"
          minHeight="600px"
          displayClose={true}
        >
          {/* ===== Filters (only when showing table) ===== */}
          {false && !editingQuestion && (
            <div className="bg-white mb-6 max-w-4xl mx-auto shadow rounded-3 p-4">
              {/* Quiz Type + Sub Quiz Type */}
              <div className="row mb-3">
                <label className="col-sm-4 col-form-label font-medium text-gray-700">
                  Quiz Type
                </label>
                {JSON.stringify(newQuestionForm)}
                <div className="col-sm-8 d-flex align-items-center gap-3">
                  {[
                    { label: "Live", value: 1 },
                    { label: "Practice", value: 0 },
                  ].map((option) => (
                    <label
                      key={option.value}
                      className="form-check-label d-flex align-items-center gap-2"
                    >
                      <input
                        type="radio"
                        className="form-check-input"
                        name="quizType"
                        value={option.value}
                        checked={quizType === option.value}
                        onChange={(e) => {
                          dispatch(
                            setState({
                              key: "quizType",
                              value: Number(e.target.value),
                            }),
                          );
                          dispatch(
                            setNewQuestionForm({ key: "theme_id", value: "" }),
                          );
                        }}
                        disabled={loading}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>

                {quizType !== "" && (
                  <>
                    <label className="col-sm-4 col-form-label font-medium text-gray-700">
                      Sub Quiz Type
                    </label>
                    <div className="col-sm-8 d-flex align-items-center gap-3 flex-wrap">
                      {quizTypesList?.data
                        ?.filter((e) => e.is_live === quizType)
                        .map((quiz) => (
                          <label
                            key={quiz.id}
                            className="form-check-label d-flex align-items-center gap-2"
                          >
                            <input
                              type="radio"
                              className="form-check-input"
                              name="subQuizType"
                              value={quiz.id}
                              checked={subQuizType == quiz.id}
                              onChange={(e) => {
                                dispatch(
                                  setState({
                                    key: "subQuizType",
                                    value: Number(e.target.value),
                                  }),
                                );
                                dispatch(
                                  setNewQuestionForm({
                                    key: "theme_id",
                                    value: "",
                                  }),
                                );
                              }}
                              disabled={loading}
                            />
                            {quiz.type.charAt(0).toUpperCase() +
                              quiz.type.slice(1)}
                          </label>
                        ))}
                    </div>
                  </>
                )}
              </div>

              {/* Class + Subject */}
              {(subQuizType === 1 || subQuizType === 2) && (
                <>
                  <div className="row mb-3">
                    <label className="col-sm-4 col-form-label font-medium text-gray-700">
                      Select Subject
                    </label>
                    <div className="col-sm-8">
                      <select
                        className="form-select"
                        name="subject_id"
                        value={newQuestionForm?.subject_id || ""}
                        onChange={handleFieldChange}
                        disabled={loading}
                      >
                        <option value="">-- Select Subject --</option>
                        {(subjectsList?.data || []).map((s) => (
                          <option key={s.subject_id} value={s.subject_id}>
                            {s.subject_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label className="col-sm-4 col-form-label font-medium text-gray-700">
                      Select Class
                    </label>
                    <div className="col-sm-8">
                      <select
                        className="form-select"
                        name="class_id"
                        value={newQuestionForm?.class_id || ""}
                        onChange={handleFieldChange}
                        disabled={loading || !newQuestionForm?.subject_id}
                      >
                        <option value="">-- Select --</option>
                        {(subjectBasedClasses?.data || []).map((s) => (
                          <option key={s.class_id} value={s.class_id}>
                            {s.class_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              )}

              {/* Theme + Group */}
              {(subQuizType === 3 || subQuizType === 4) && (
                <>
                  <div className="row mb-3">
                    <label className="col-sm-4 col-form-label text-gray-700">
                      Theme
                    </label>
                    <div className="col-sm-8">
                      <select
                        className="form-select"
                        name="theme_id"
                        value={newQuestionForm?.theme_id || ""}
                        onChange={handleThemeChange}
                        disabled={loading}
                      >
                        <option value="">-- Select Theme --</option>
                        {themes.map((theme) => (
                          <option key={theme.theme_id} value={theme.theme_id}>
                            {theme.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {newQuestionForm?.theme_id && subThemes?.length > 0 && (
                    <div className="row mb-3">
                      <label className="col-sm-4 col-form-label text-gray-700">
                        Sub Themes
                      </label>
                      <div className="col-sm-8">
                        <select
                          className="form-select"
                          value={selectedSubThemes?.[0] || ""}
                          onChange={(e) => {
                            dispatch(
                              setState({
                                key: "selectedSubThemes",
                                value: [Number(e.target.value)],
                              }),
                            );
                          }}
                        >
                          <option value="">Select Sub Theme</option>
                          {subThemes.map((e) => (
                            <option key={e.theme_id} value={e.theme_id}>
                              {e.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  <div className="row mb-3">
                    <label className="col-sm-4 col-form-label font-medium text-gray-700">
                      Groups
                    </label>
                    <div className="col-sm-8">
                      <select
                        className="form-select"
                        name="group_id"
                        value={newQuestionForm?.group_id || ""}
                        onChange={handleFieldChange}
                        disabled={loading}
                      >
                        <option value="">-- Select Group --</option>
                        {groups?.data?.map((g) => (
                          <option key={g.group_id} value={g.group_id}>
                            {g.group_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              )}

              {/* Difficulty */}
              <div className="row mb-3">
                <label className="col-sm-4 col-form-label font-medium text-gray-700">
                  Select Difficulty Level
                </label>
                <div className="col-sm-8">
                  <select
                    className={`form-control ${!subQuizType ? "opacity-50 cursor-not-allowed" : ""}`}
                    name="difficulty_level"
                    value={newQuestionForm?.difficulty_level || ""}
                    onChange={handleFieldChange}
                    disabled={!subQuizType || loading}
                  >
                    <option value="">-- Select --</option>
                    {difficultyOptions.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
          <div className="bg-white mb-3 max-w-4xl mx-auto shadow-sm rounded-3 border p-3">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <span className="fw-semibold text-dark me-2">Quiz:</span>

              <span className="badge bg-primary-subtle text-primary">
                {Number(quizType) === 1 ? "Live" : "Practice"}
              </span>

              <span className="text-muted">|</span>

              <span className="text-secondary small">Sub Type:</span>
              <span className="fw-medium small">
                {quizTypesList?.data?.find(
                  (q) => Number(q.id) === Number(subQuizType),
                )?.type || "-"}
              </span>

              {(Number(subQuizType) === 1 || Number(subQuizType) === 2) && (
                <>
                  <span className="text-muted">|</span>
                  <span className="text-secondary small">Subject:</span>
                  <span className="fw-medium small">
                    {subjectsList?.data?.find(
                      (s) =>
                        String(s.subject_id) ===
                        String(newQuestionForm?.subject_id),
                    )?.subject_name || "-"}
                  </span>

                  <span className="text-muted">|</span>
                  <span className="text-secondary small">Class:</span>
                  <span className="fw-medium small">
                    {subjectBasedClasses?.data?.find(
                      (c) =>
                        String(c.class_id) ===
                        String(newQuestionForm?.class_id),
                    )?.class_name || "-"}
                  </span>
                </>
              )}

              {(Number(subQuizType) === 3 || Number(subQuizType) === 4) && (
                <>
                  <span className="text-muted">|</span>
                  <span className="text-secondary small">Theme:</span>
                  <span className="fw-medium small">
                    {themes?.find(
                      (t) =>
                        String(t.theme_id) ===
                        String(newQuestionForm?.theme_id),
                    )?.name || "-"}
                  </span>

                  <span className="text-muted">|</span>
                  <span className="text-secondary small">Group:</span>
                  <span className="fw-medium small">
                    {groups?.data?.find(
                      (g) =>
                        String(g.group_id) ===
                        String(newQuestionForm?.group_id),
                    )?.group_name || "-"}
                  </span>
                </>
              )}

              <span className="text-muted">|</span>
              <span className="text-secondary small">Difficulty:</span>
              <span className="fw-medium small">
                {difficultyOptions?.find(
                  (d) =>
                    String(d.id) === String(newQuestionForm?.difficulty_level),
                )?.name || "-"}
              </span>
            </div>
          </div>

          {/* ===== TABLE or EDIT FORM ===== */}
          {editingQuestion ? (
            // ---------- INLINE EDIT FORM ----------
            <div className="bg-white p-4 rounded shadow max-w-3xl mx-auto">
              <div className="mb-3">
                <label className="form-label fw-semibold">Question</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={editingQuestion.question || ""}
                  onChange={(e) =>
                    setEditingQuestion({
                      ...editingQuestion,
                      question: e.target.value,
                    })
                  }
                />
              </div>

              <div className="row">
                {["A", "B", "C", "D"].map((opt) => (
                  <div className="col-md-6 mb-3" key={opt}>
                    <label className="form-label fw-semibold">
                      Option {opt}
                    </label>
                    <input
                      className="form-control"
                      value={editingQuestion[`option_${opt}`] || ""}
                      onChange={(e) =>
                        setEditingQuestion({
                          ...editingQuestion,
                          [`option_${opt}`]: e.target.value,
                        })
                      }
                    />
                  </div>
                ))}
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Correct Option</label>
                <select
                  className="form-select"
                  value={editingQuestion.correct_option || ""}
                  onChange={(e) =>
                    setEditingQuestion({
                      ...editingQuestion,
                      correct_option: e.target.value,
                    })
                  }
                >
                  <option value="">-- Select --</option>
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                  <option value="D">D</option>
                </select>
              </div>

              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  className="btn btn-secondary px-4"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary px-4"
                  onClick={handleUpdateQuestion}
                >
                  Save Changes
                </button>
              </div>
            </div>
          ) : // ---------- TABLE ----------
          questions.length > 0 ? (
            <>
              <QuestionsTable
                data={questions}
                onEdit={(question) => setEditingQuestion({ ...question })}
                onDelete={handleDeleteQuestion}
              />
              <div className="d-flex justify-content-end mt-4 gap-3">
                {/* <button
                  className="btn btn-secondary px-4"
                  onClick={onClose}
                  disabled={submitting}
                >
                  Close
                </button> */}
                <button
                  className="btn btn-success px-4"
                  onClick={handleSubmitReviewedQuestions}
                  disabled={submitting || loading}
                >
                  {submitting ? "Submitting..." : "Submit Reviewed Questions"}
                </button>
              </div>
            </>
          ) : (
            <div className="text-center text-muted py-5">
              No questions to preview
            </div>
          )}
        </Popup>
      )}
    </>
  );
}
